import { CaseOrganizationExecutionMode, caseOrganizationHandlingLabels } from './caseOrganizationPlan';
import { ManifestWorkbookRows } from './manifestWorkbook';

export interface ManifestWorkbookContext {
  studyId: string;
  labId: string;
  studyName: string | null;
  labName: string | null;
  handling: CaseOrganizationExecutionMode;
}

export type ManifestWorkbookContextResult =
  | { status: 'absent' }
  | { status: 'valid'; context: ManifestWorkbookContext }
  | { status: 'invalid' };

const CONTEXT_VERSION = 1;
const CONTEXT_KEYS = new Set(['formatVersion', 'studyId', 'labId', 'studyName', 'labName', 'handling']);
const isDisplayName = (value: unknown): value is string | null => value === null || typeof value === 'string';

export const decodeManifestWorkbookContext = (
  rows: readonly unknown[] | null | undefined
): ManifestWorkbookContextResult => {
  if (rows == null) return { status: 'absent' };
  const invalid = (): ManifestWorkbookContextResult => ({ status: 'invalid' });
  const values = new Map<string, unknown>();
  for (const row of rows) {
    if (!Array.isArray(row) || row.length < 1 || row.length > 2 || typeof row[0] !== 'string') {
      return invalid();
    }
    const key = row[0];
    if (!CONTEXT_KEYS.has(key)) return invalid();
    if (values.has(key)) return invalid();
    values.set(key, row[1] ?? null);
  }
  for (const key of CONTEXT_KEYS) {
    if (!values.has(key)) return invalid();
  }
  if (values.get('formatVersion') !== CONTEXT_VERSION) return invalid();

  const studyId = values.get('studyId');
  const labId = values.get('labId');
  const studyName = values.get('studyName');
  const labName = values.get('labName');
  const handling = values.get('handling');
  if (
    typeof studyId !== 'string' ||
    !studyId.trim() ||
    studyId !== studyId.trim() ||
    typeof labId !== 'string' ||
    !labId.trim() ||
    labId !== labId.trim()
  )
    return invalid();
  if (!isDisplayName(studyName) || !isDisplayName(labName)) {
    return invalid();
  }
  if (typeof handling !== 'string' || !Object.prototype.hasOwnProperty.call(caseOrganizationHandlingLabels, handling)) {
    return invalid();
  }
  return {
    status: 'valid',
    context: { studyId, labId, studyName, labName, handling: handling as CaseOrganizationExecutionMode },
  };
};

export const encodeManifestWorkbookContext = (context: ManifestWorkbookContext): ManifestWorkbookRows => {
  const rows: ManifestWorkbookRows = [
    ['formatVersion', CONTEXT_VERSION],
    ['studyId', context.studyId],
    ['labId', context.labId],
    ['studyName', context.studyName],
    ['labName', context.labName],
    ['handling', context.handling],
  ];
  const decoded = decodeManifestWorkbookContext(rows);
  return decoded.status === 'valid' ? rows : [];
};
